import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PersonalFormSC, {
  PersonalFormTextInputSC,
  PersonalFormErrorSC,
} from './personal-form.styled';
import { Formik } from 'formik';
import PersonalFormSchema from './personal-form-rules';
import { BodyText, BiggerText } from '../../typography';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import dlt from '../../../utils/default-lang';
import Button from '../../ui/button';

const _PersonalForm = ({ onSubmitForm, userData, disabled }) => {
  const [formInitialValues, setFormInitialValues] = useState({
    first_name: '',
    last_name: '',
    birth_number: '',
    phone: '',
    email: '',
    email_confirmation: '',
  });

  useEffect(() => {
    if (userData.id) {
      setFormInitialValues({
        first_name: userData.first_name ? userData.first_name : '',
        last_name: userData.last_name ? userData.last_name : '',
        birth_number: userData.personal_identification_number
          ? userData.personal_identification_number
          : '',
        phone: userData.phone_number ? userData.phone_number : '',
        email: userData.email ? userData.email : '',
        email_confirmation: userData.email ? userData.email : '',
      });
    }
  }, [userData]);

  return (
    <PersonalFormSC>
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={PersonalFormSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          onSubmitForm(values);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
          const firstNameHasError = errors && errors.first_name && touched.first_name;
          const lastNameHasError = errors && errors.last_name && touched.last_name;
          const birthNumberHasError = errors && errors.birth_number && touched.birth_number;
          const phoneHasError = errors && errors.phone && touched.phone;
          const emailHasError = errors && errors.email && touched.email;
          const emailConfirmationHasError =
            errors && errors.email_confirmation && touched.email_confirmation;

          return (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="heading-block">
                  <BodyText className="bolder">
                    <FormattedMessage
                      id="onboarding_personal_form_headline_1"
                      defaultMessage={dlt['onboarding_personal_form_headline_1']}
                    />
                  </BodyText>
                  <div className="separator"></div>
                </div>
                <div className="half-block">
                  <FormattedMessage
                    id="onboarding_personal_form_first_name_placeholder"
                    defaultMessage={dlt['onboarding_personal_form_first_name_placeholder']}
                  >
                    {(placeholder) => (
                      <PersonalFormTextInputSC
                        type="text"
                        name="first_name"
                        placeholder={placeholder}
                        filled={values.first_name}
                        hasError={firstNameHasError}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.first_name || ''}
                      />
                    )}
                  </FormattedMessage>
                  {firstNameHasError ? (
                    <PersonalFormErrorSC>{errors.first_name}</PersonalFormErrorSC>
                  ) : null}
                </div>
                <div className="half-block">
                  <FormattedMessage
                    id="onboarding_personal_form_last_name_placeholder"
                    defaultMessage={dlt['onboarding_personal_form_last_name_placeholder']}
                  >
                    {(placeholder) => (
                      <PersonalFormTextInputSC
                        type="text"
                        name="last_name"
                        placeholder={placeholder}
                        filled={values.last_name}
                        hasError={lastNameHasError}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.last_name || ''}
                      />
                    )}
                  </FormattedMessage>
                  {lastNameHasError ? (
                    <PersonalFormErrorSC>{errors.last_name}</PersonalFormErrorSC>
                  ) : null}
                </div>
                <div className="full-block">
                  <FormattedMessage
                    id="onboarding_personal_form_birth_number_placeholder"
                    defaultMessage={dlt['onboarding_personal_form_birth_number_placeholder']}
                  >
                    {(placeholder) => (
                      <PersonalFormTextInputSC
                        type="text"
                        name="birth_number"
                        placeholder={placeholder}
                        filled={values.birth_number}
                        hasError={birthNumberHasError}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.birth_number || ''}
                      />
                    )}
                  </FormattedMessage>
                  {birthNumberHasError ? (
                    <PersonalFormErrorSC>{errors.birth_number}</PersonalFormErrorSC>
                  ) : null}
                </div>
              </div>
              <div className="row">
                <div className="heading-block">
                  <BodyText className="bolder">
                    <FormattedMessage
                      id="onboarding_personal_form_headline_2"
                      defaultMessage={dlt['onboarding_personal_form_headline_2']}
                    />
                  </BodyText>
                  <div className="separator"></div>
                </div>
                <div className="full-block">
                  <FormattedMessage
                    id="onboarding_personal_form_phone_placeholder"
                    defaultMessage={dlt['onboarding_personal_form_phone_placeholder']}
                  >
                    {(placeholder) => (
                      <PersonalFormTextInputSC
                        type="text"
                        name="phone"
                        placeholder={placeholder}
                        filled={values.phone}
                        hasError={phoneHasError}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone || ''}
                      />
                    )}
                  </FormattedMessage>
                  {phoneHasError ? <PersonalFormErrorSC>{errors.phone}</PersonalFormErrorSC> : null}
                </div>
                <div className="full-block">
                  <FormattedMessage
                    id="onboarding_personal_form_email_placeholder"
                    defaultMessage={dlt['onboarding_personal_form_email_placeholder']}
                  >
                    {(placeholder) => (
                      <PersonalFormTextInputSC
                        type="text"
                        name="email"
                        placeholder={placeholder}
                        filled={values.email}
                        hasError={emailHasError}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email || ''}
                      />
                    )}
                  </FormattedMessage>
                  {emailHasError ? <PersonalFormErrorSC>{errors.email}</PersonalFormErrorSC> : null}
                </div>
                <div className="full-block">
                  <FormattedMessage
                    id="onboarding_personal_form_email_confirm_placeholder"
                    defaultMessage={dlt['onboarding_personal_form_email_confirm_placeholder']}
                  >
                    {(placeholder) => (
                      <PersonalFormTextInputSC
                        type="text"
                        name="email_confirmation"
                        placeholder={placeholder}
                        filled={values.email_confirmation}
                        hasError={emailConfirmationHasError}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email_confirmation || ''}
                        onPaste={(e) => e.preventDefault()}
                      />
                    )}
                  </FormattedMessage>
                  {emailConfirmationHasError ? (
                    <PersonalFormErrorSC>{errors.email_confirmation}</PersonalFormErrorSC>
                  ) : null}
                </div>
              </div>
              <div className="row">
                <div className="full-block">
                  <BiggerText>
                    <FormattedHTMLMessage
                      id="onboarding_continue_hint"
                      defaultMessage={dlt['onboarding_continue_hint']}
                    />
                  </BiggerText>
                  <Button
                    type="PRIMARY"
                    className="form-submit"
                    onClick={handleSubmit}
                    withIcon
                    disabled={disabled}
                  >
                    <FormattedMessage
                      id="global_continue"
                      defaultMessage={dlt['global_continue']}
                    />
                  </Button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </PersonalFormSC>
  );
};

const mapStateToProps = (state) => {
  return {
    userData: state.user,
  };
};

const PersonalForm = connect(mapStateToProps)(_PersonalForm);

export default PersonalForm;
